// imsFormDataTransformations.ts
import { Group, Category, ImsFormsData, ComponentResponse } from './imsFormHelpers';

export const populateInitialValues = (groups: any[]) => {
    const initialValues: { [key: string]: boolean | string | number } = {};
    
    groups.forEach((group: { components: any[] }) => {
      group.components.forEach(
        (component: {
          control_type: string
          id: string | number
          component_response: any
          values: any[]
        }) => {
          const key = component.id.toString();
  
          if (component.control_type === 'checkbox') {
            // Convert to real boolean
            initialValues[key] =
              component.component_response === true ||
              component.component_response === 'true';
          } else if (component.control_type === 'checkboxgroup') {
            // Store values for each checkbox group item
            component.values.forEach((value: any) => {
              const subKey = value.id.toString();
              initialValues[subKey] =
                value.component_response === true ||
                value.component_response === 'true';
            });
          } else {
            // Handle fields like textfield, number, etc.
            const response = component.component_response;

            if (
              response &&
              typeof response === 'object' &&
              'value' in response
            ) {
              const val = (response as any).value;
              initialValues[key] =
                (component as any).datatype === 'number' && (val === 0 || val === '0')
                  ? ''
                  : val ?? '';
            } else {
              initialValues[key] =
                (component as any).datatype === 'number' && (response === 0 || response === '0')
                  ? ''
                  : response ?? '';
            }
          }
        }
      );
    });
  
    return initialValues;
};

export const prepareImsFormsData = (
  formData: any,
  selectedCategory: any,
  nominationValue: string,
  jobCodeData: string,
  opportunityID: string | null
): ImsFormsData => {
  const ims_forms_data: ImsFormsData = {
    id: 0,
    groups: [],
    category: selectedCategory?.category || '',
    nomination: '0',
    opportunity_id: opportunityID ? opportunityID : undefined,
    jobcode: '',
  };

  // Update the structure based on the received form data
  if (formData.id) {
    ims_forms_data.id = selectedCategory?.id || 0;
    ims_forms_data.nomination = nominationValue;
    ims_forms_data.jobcode = jobCodeData;

    formData.Categories.forEach(
      (category: { id: any; category: any; Groups: any[] }) => {
        const categoryObj: Category = {
          id: category.id || 0,
          title: category.category || '',
          groups: [],
        };

        category.Groups.forEach(
          (group: { id: any; title: any; Components: any[] }) => {
            const groupObj: Group = {
              id: group.id || 0,
              title: group.title || '',
              components: group.Components?.map(
                (component: {
                  component_response: string | { value: string; photo: string }
                  photo_capture: number
                  id: any
                  label: any
                  label_display: any
                  limit: any
                  order: any
                  Values: any[]
                  prefix: any
                  suffix: any
                  datatype: any
                  group_id: any
                  required: any
                  max_value: any
                  min_value: any
                  char_count: any
                  max_length: any
                  min_length: any
                  helper_text: any
                  control_type: any
                  component_form: any
                  barcode_scanning: any
                  is_auto_populated: boolean
                }) => {
                  // Define the response based on photo_capture
                  let componentResponse: ComponentResponse | string;
                  if (component.photo_capture === 1) {
                    componentResponse = {
                      value: (component.component_response as { value: string })?.value || '',
                      photo: (component.component_response as { photo: string })?.photo || '',
                    };
                  } else {
                    componentResponse = '';
                  }

                  return {
                    id: component.id || 0,
                    label: component.label || '',
                    label_display: component.label_display || '',
                    limit: component.limit || 0,
                    order: component.order || 0,
                    values: component.Values?.map(
                      (value: {
                        id: any
                        order: any
                        value: any
                        component_id: any
                        component_response: any
                      }) => ({
                        id: value.id || 0,
                        order: value.order || 0,
                        value: value.value || '',
                        component_id: value.component_id || 0,
                        component_response: value.component_response || '',
                      })
                    ) || [],
                    prefix: component.prefix || '',
                    suffix: component.suffix || '',
                    datatype: component.datatype || '',
                    group_id: component.group_id || 0,
                    required: component.required || '',
                    max_value: component.max_value || 0,
                    min_value: component.min_value || 0,
                    char_count: component.char_count || 0,
                    max_length: component.max_length || 0,
                    min_length: component.min_length || 0,
                    helper_text: component.helper_text || '',
                    control_type: component.control_type || '',
                    photo_capture: component.photo_capture || 0,
                    component_form: component.component_form || [],
                    barcode_scanning: component.barcode_scanning || 0,
                    component_response: componentResponse,
                    is_auto_populated: component.is_auto_populated || false,
                  }
                }
              ) || [],
              control_type: '',
              component_response: undefined,
              values: []
            };

            categoryObj.groups.push(groupObj);
          }
        );

        ims_forms_data.groups.push(...categoryObj.groups);
      }
    );
  }

  return ims_forms_data;
};

export const processFormData = (formData: any) => {
  const newData = { ...formData };

  // Check if Categories array exists and is an array
  if (formData.Categories && Array.isArray(formData.Categories)) {
    // Iterate through categories to sort components
    newData.Categories.forEach(
      (category: { Components: any[]; Groups: any[] }) => {
        // Sort components by id
        category.Components.sort(
          (a: { id: number }, b: { id: number }) => a.id - b.id
        );

        if (category.Groups && Array.isArray(category.Groups)) {
          category.Groups.forEach((group: { Components: any[] }) => {
            // Sort components by order_id
            group.Components.sort(
              (a: { order: number }, b: { order: number }) =>
                a.order - b.order
            );

            // Filter components based on the category components
            if (group.Components && Array.isArray(group.Components)) {
              group.Components = group.Components.filter(
                (component: { group_id: any; id: any }) => {
                  return category.Components.some(
                    (categoryComponent: { group_id: any; id: any }) => {
                      return (
                        component.group_id === categoryComponent.group_id &&
                        component.id === categoryComponent.id
                      );
                    }
                  );
                }
              );
            }
          });
        }
      }
    );
  } else {
    console.error('Categories array is missing or not an array.');
  }

  return newData;
};


export const prepareUpdatedImsData = (imsFormData: any) => {
  const new_ims_forms_data = { ...imsFormData };

  // Iterate over each group and their components
  new_ims_forms_data.groups = new_ims_forms_data?.groups?.map(
    (group: { components: any[] }) => {
      group.components = group.components.map(
        (component: {
          control_type: string
          photo_capture: number
          component_response: { value: any; photo: any }
          photo: any
        }) => {
          // Check if the component meets the criteria
          if (
            component.control_type === 'subheading' &&
            component.photo_capture === 1
          ) {
            // Check if component_response is an object
            if (
              component.component_response &&
              typeof component.component_response === 'object'
            ) {
              // Copy the photo key from component_response to the component itself
              component.photo = component.component_response.photo;
            }
          }
          return component;
        }
      );
      return group;
    }
  );

  return new_ims_forms_data;
};